"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePathname, useRouter } from "next/navigation";

import {
  OpenAccountEmailContextType,
  OpenAccountUser,
} from "@/types/openAccount";
import { RecaptchaResponse } from "@/types/recaptcha";
import { UserEmail } from "@/types/user";

import { httpRequest } from "@/lib/http";

import { useOpenAccount } from "@/context/openAccount";
import { useRecaptcha } from "@/context/util/recaptcha";
import { useSecuritySettings } from "@/context/util/securitySettings";
import { useUi } from "@/context/util/ui";

import openAccountAlerts from "@/components/alerts/openAccount";
import CountdownTimer from "@/components/countdown";

const EmailContext = createContext<OpenAccountEmailContextType>(null!);

export function EmailProvider({ children }: { children: ReactNode }) {
  const { handleSetAlert, setLoading } = useUi();
  const { recaptchaRequest } = useRecaptcha();
  const { getSecurityQuestions, timer, timerSpan } = useSecuritySettings();
  const { openAccountUser, setActiveStep, setOpenAccountUser } =
    useOpenAccount();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const timerElement = timerSpan.current;
    if (!timerElement) return;

    if (timerElement.textContent === "0 minutes 0 seconds") {
      const userIndicator = openAccountUser?.userIndicator ?? "EM";

      // show expired token no matter where the user is in the process
      handleSetAlert(
        [0, 1, 2],
        openAccountAlerts.getExpiredTokenAlert(userIndicator)
      );

      setActiveStep(1);

      timer.current = null;
      timerSpan.current = null;
    }
  }, [openAccountUser?.userIndicator, openAccountUser?.token]);

  const sendVerificationEmail = useCallback(
    (email: UserEmail, token: string, username: string) => {
      const request = async (init?: RequestInit) => {
        window.scrollTo(0, 0);
        setLoading(true);

        try {
          const url = `/account-utilities/api/open/verify`;

          const id = email.id;

          const body = JSON.stringify({ id, token, username });
          const headers = { ...(init?.headers ?? {}) };
          const method = "POST";

          const requestInit: RequestInit = { body, headers, method };

          const sendEmailVerificationResponse = (
            await httpRequest<{ message: string } | RecaptchaResponse>(
              url,
              requestInit
            )
          ).data;

          if (!sendEmailVerificationResponse)
            throw new Error("Empty http response");

          if ("valid" in sendEmailVerificationResponse) {
            handleSetAlert([0, 1], openAccountAlerts.getRecaptchaAlert());
            return sendEmailVerificationResponse;
          }

          setOpenAccountUser(
            (prev) => ({ ...(prev ?? {}), email } as OpenAccountUser)
          );

          handleSetAlert(
            [0, 1],
            openAccountAlerts.getSendEmailSuccessAlert(
              email.address,
              openAccountUser?.userIndicator ?? "EM",
              timer.current
            )
          );
        } catch (error) {
          const message =
            "Failed to send account verification email. Please try again later.";
          console.error(message, error);

          handleSetAlert(
            [0, 1],
            openAccountAlerts.getGeneralErrorAlert(
              message,
              error,
              openAccountUser?.userIndicator ?? "EM"
            )
          );
        } finally {
          setLoading(false);
        }
      };

      return recaptchaRequest("open_account_email_verification", request);
    },
    [openAccountUser, recaptchaRequest]
  );

  // advances step to 2
  const validateEmailToken = useCallback(
    (token: string) => {
      const request = async (init?: RequestInit) => {
        window.scrollTo(0, 0);
        setLoading(true);

        let recaptchaValid = true;

        try {
          const url = `/account-utilities/api/open/verify?token=${token}`;

          const requestInit: RequestInit = {
            headers: { ...(init?.headers ?? {}) },
            method: "GET",
          };

          const openAccountData = (
            await httpRequest<OpenAccountUser | RecaptchaResponse>(
              url,
              requestInit
            )
          ).data;

          if (!openAccountData) throw new Error("Empty http response");

          if ("valid" in openAccountData) {
            recaptchaValid = false;

            handleSetAlert([0], openAccountAlerts.getRecaptchaAlert());
            return openAccountData;
          }

          setOpenAccountUser((prev) => ({
            ...(prev ?? {}),
            ...openAccountData,
            verified: true,
          }));

          timer.current = (
            <CountdownTimer<OpenAccountUser>
              fields={["token", "verified"]}
              ref={timerSpan}
              setExpired={setOpenAccountUser}
              targetDate={openAccountData.tokenExpiration}
            />
          );

          handleSetAlert(
            [2],
            openAccountAlerts.getVerificationSuccessAlert(
              openAccountUser?.userIndicator ?? "EM",
              timer.current
            )
          );

          getSecurityQuestions();
          setActiveStep(2);
        } catch (error) {
          const message =
            "Failed to validate open account token. Please try again.";
          console.error(message, error);

          handleSetAlert(
            [0],
            openAccountAlerts.getTokenValidationErrorAlert(
              message,
              error,
              openAccountUser?.userIndicator ?? "EM",
              timer.current
            )
          );
        } finally {
          if (recaptchaValid) router.push(pathname);
          else setActiveStep(0);
          setLoading(false);
        }
      };

      return recaptchaRequest("validate_open_account_token", request);
    },
    [pathname, recaptchaRequest, router]
  );

  return (
    <EmailContext.Provider
      value={{
        sendVerificationEmail,
        validateEmailToken,
      }}
    >
      {children}
    </EmailContext.Provider>
  );
}

export function useEmailApi() {
  const ctx = useContext(EmailContext);
  if (!ctx) throw new Error("useEmailApi must be used within a EmailContext");
  return ctx;
}
